import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import RootStore from '@App/stores/root-store'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { SearchResultPartyDetails } from '@Entities/party-details'
import { Works } from '@Entities/work/works'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { getNumTitles, INumTitles } from '@App/helpers/pagination/get-num-titles'
import { ALPHABETICAL_WORK_TITLE_SORT } from '@App/const/pagination'
import { QueryPartyStore } from '@App/stores/search/party/query-party-store'
import PartySearchState from '@App/stores/search/public/party-search-state'

/**
 * What the public writer and publisher searches share: a party search (name / IPI), then the works of the party
 * picked from it. The subclasses supply only the two API calls, their criteria options and the label.
 */
export default abstract class PublicSearchPartyStore<Q extends object>
    extends PartySearchState<Q>
    implements QueryPartyStore<SearchResultPartyDetails, Q>
{
    abstract readonly partySearchScope: string

    protected abstract readonly partyLabel: string

    private _secondaryPage = 1

    private _secondarySearchResults: Works | undefined

    private _secondarySearchPaginationInfo: PaginationInfo | undefined

    private _secondaryResultsPerPageLimit = 10

    private _selectedParty: SearchResultPartyDetails | undefined

    private _showWorks = false

    constructor(rootStore: RootStore) {
        super(rootStore)
        makeObservable<
            PublicSearchPartyStore<Q>,
            | '_secondarySearchResults'
            | '_secondaryResultsPerPageLimit'
            | '_secondaryPage'
            | '_showWorks'
            | '_selectedParty'
            | 'resetWorks'
        >(this, {
            _secondarySearchResults: observable,
            _secondaryResultsPerPageLimit: observable,
            _secondaryPage: observable,
            _showWorks: observable,
            _selectedParty: observable,
            secondaryNumTitles: computed,
            worksResultMessage: computed,
            setShowWorks: action,
            resetWorks: action,
            applyPartyWorks: action,
            selectParty: action,
            setSecondaryPage: action,
            setSecondaryResultsPerPage: action,
        })
        this.resetStore()
    }

    protected abstract searchWorksForParty(party: SearchResultPartyDetails, pagination: PaginationBody): Promise<Works>

    get secondarySearchPaginationInfo(): PaginationInfo | undefined {
        return this._secondarySearchPaginationInfo
    }

    get secondaryResultsPerPageLimit(): number {
        return this._secondaryResultsPerPageLimit
    }

    get secondarySearchResults(): Works | undefined {
        return this._secondarySearchResults
    }

    get showWorks(): boolean {
        return this._showWorks
    }

    get secondaryPage(): number {
        return this._secondaryPage
    }

    get selectedParty(): SearchResultPartyDetails | undefined {
        return this._selectedParty
    }

    get selectedPartyId(): string | undefined {
        return this._selectedParty?.ipId ?? undefined
    }

    get restoredPartyWorks(): Works | undefined {
        return this._selectedParty ? this._secondarySearchResults : undefined
    }

    get secondaryNumTitles(): INumTitles {
        return getNumTitles(
            this._secondarySearchResults?.count ?? 0,
            this._secondarySearchPaginationInfo?.currentPage ?? 1,
            this._secondaryResultsPerPageLimit
        )
    }

    get worksResultMessage(): string {
        if (this._selectedParty?.name) {
            return `Works for ${this._selectedParty.name}`
        }
        const ipi = (this.partyQuery as { ipiNameNumber?: string }).ipiNameNumber
        return ipi ? `Works for ${this.partyLabel} IPI: ${ipi}` : 'Works'
    }

    setShowWorks(showWorks: boolean): void {
        this._showWorks = showWorks
    }

    // Imperative flow, still used by the pages that haven't moved to Query (songwriter, catalog transfer).
    async onChangeSecondarySearchPage(pagination: PaginationBody): Promise<void> {
        if (!this._selectedParty) {
            return
        }
        this.rootStore.preloaderStore.openTransparentPreloader(true)
        this.resetWorks()
        try {
            this.applyPartyWorks(await this.fetchPartyWorks(this._selectedParty, pagination), pagination)
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
        this.rootStore.preloaderStore.openTransparentPreloader(false)
    }

    async secondarySearch(party: SearchResultPartyDetails): Promise<void> {
        if (party.ipId !== this._selectedParty?.ipId) {
            runInAction(() => (this._selectedParty = party))
            await this.onChangeSecondarySearchPage({ page: 1, limit: this._secondaryResultsPerPageLimit })
        }
        this.setShowWorks(true)
    }

    async secondarySetPageLimit(newLimit: number): Promise<void> {
        runInAction(() => (this._secondaryResultsPerPageLimit = newLimit))
        await this.onChangeSecondarySearchPage({ page: 1, limit: newLimit })
    }

    fetchPartyWorks(party: SearchResultPartyDetails, pagination: PaginationBody): Promise<Works> {
        return this.searchWorksForParty(party, { sort: ALPHABETICAL_WORK_TITLE_SORT, ...pagination })
    }

    applyPartyWorks(works: Works, pagination: PaginationBody): void {
        this._secondarySearchResults = works
        if (pagination.limit && pagination.page) {
            this._secondarySearchPaginationInfo = calcNumPages(
                works.count,
                pagination.limit,
                pagination.page,
                pagination.refined
            )
        }
    }

    /** Picks a party and shows its works, from page 1; picking the one already showing keeps its page. */
    selectParty(party: SearchResultPartyDetails): void {
        if (party.ipId !== this._selectedParty?.ipId) {
            this.resetWorks()
            this._selectedParty = party
            this._secondaryPage = 1
        }
        this._showWorks = true
    }

    setSecondaryPage(page: number): void {
        this._secondaryPage = page
    }

    setSecondaryResultsPerPage(limit: number): void {
        this._secondaryResultsPerPageLimit = limit
        this._secondaryPage = 1
    }

    protected resetSecondary(): void {
        this._showWorks = false
        this._selectedParty = undefined
        this._secondaryResultsPerPageLimit = 10
        this._secondaryPage = 1
        this.resetWorks()
    }

    private resetWorks(): void {
        this._secondarySearchResults = undefined
        this._secondarySearchPaginationInfo = undefined
    }
}
